import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Badge } from '@blueprint/badge';
import { Image, Card, CardHeading3, CardImage, Link } from '@blueprint/ui';

const StyledCard = styled(Card)(
    (props) => css`
        display: flex;
        flex-direction: column;
        border: 1px solid ${props.theme.colors['tone-blue-t1']};
        border-radius: ${props.theme.borderRadius['8px']};
        padding: 0;
        height: 100%;

        ${props.theme.mq.medium} {
            margin-top: auto;
            border-radius: ${props.theme.borderRadius['300']};
        }

        ${CardHeading3} {
            margin: 0 0 ${props.theme.spacing['8px']} 0;
        }

        ${CardImage} {
            margin-top: 0;
            margin-left: 0;
            min-width: 100%;
            height: 100%;
        }
    `
);

const PropertyImage = styled(Link)`
    display: block;
    width: 100%;
    height: 11.125rem; /* 178px */
    flex-shrink: 0;
`;

const PropertyDetails = styled(Link)(
    () => css`
        display: flex;
        flex-direction: column;
        align-self: stretch;
        flex: 1;
        padding: 12px 12px 0;
    `
);

const TitleWrapper = styled('div')(
    () => css`
        display: flex;
        flex-flow: column wrap;
    `
);

const StyledPropertyPrice = styled('p')(
    ({ theme: { fontSizes, spacing, type, colors } }) => css`
        margin: 0 0 ${spacing['4px']} 0;
        line-height: ${fontSizes[6]};
        font-size: ${type['7']};
        color: ${colors['text-title']};
        font-weight: ${type.semiBold};
    `
);

const StyledPropertyPriceSmall = styled('p')(
    ({ theme: { fontSizes, spacing, type, colors } }) => css`
        margin: 0 0 ${spacing['4px']} 0;
        line-height: ${fontSizes[6]};
        font-size: ${type['6']};
        color: ${colors['text-title']};
        font-weight: ${type.semiBold};
    `
);

const StyledPropertySubPrice = styled('p')(
    ({ theme: { spacing, type, colors } }) => css`
        margin: -${spacing['4px']} 0 ${spacing['4px']} 0;
        line-height: ${type.lineHeight};
        font-size: ${type.secondary};
        font-weight: ${type.semiBold};
        color: ${colors['text-placeholder']};
    `
);

const StyledPropertyTitle = styled('p')(
    ({ theme }) => css`
        margin: 0 0 ${theme.spacing['4px']} 0;
        font-size: ${theme.type.secondary};
        color: ${theme.colors['text-placeholder']};
        font-weight: ${theme.type.semiBold};
        line-height: 20px;
    `
);

const StyledPropertyAddress = styled('p')(
    ({ theme }) => css`
        margin: 0;
        font-size: ${theme.type.secondary};
        color: ${theme.colors['text-title']};
        line-height: 20px;
    `
);

const StyledPropertyAddressLarge = styled('p')(
    ({ theme }) => css`
        margin: 0;
        font-size: ${theme.type['8']};
        color: ${theme.colors['text-title']};
        line-height: 20px;
    `
);

const AgentImageWrapper = styled('div')(
    (props) => css`
        ${props.theme.mq.medium} {
            margin-top: auto;
        }
        line-height: 0;
        padding: 0 12px 12px;
    `
);

const AgentImage = styled(Image)(
    (props) => css`
        width: auto;
        height: auto;
        max-height: calc(${props.theme.spacing['80px']} / 2);
        align-self: flex-start;
        margin: auto 0;
    `
);

const PropertyDetailsContainer = styled('div')(
    (props) => css`
        display: flex;
        flex-flow: column nowrap;
        height: 100%;
        background: ${props.theme.colors.white};
        margin-top: ${props.theme.spacing['8px']};

        ${props.theme.mq.medium} {
            margin-top: 0;
        }
    `
);

const PropertyWrapper = styled('div')(
    (props) => css`
        display: none;
        flex-flow: row wrap;
        padding: ${props.theme.spacing['16px']};
        margin-bottom: 2rem;
        background: ${props.theme.colors['tone-blue-t3']};
        border-radius: ${props.theme.borderRadius['8px']};

        ${props.theme.mq.medium} {
            display: flex;
        }

        ${props.theme.mq.extraLarge} {
            padding: calc(${props.theme.spacing['24px']} / 2);
        }
    `
);

const FeaturePropertyCardWrapper = styled('div')(
    (props) => css`
            display: inline-block;
            width: calc((100% - (12px * 2)) / 3);
            height: auto;
            margin-right: calc(${props.theme.spacing['24px']} / 2);

            :focus {
                box-shadow: 0 0 0 3px ${props.theme.colors['tone-blue-t2']};
            }

            ${props.theme.mq.extraLarge} {
                width: calc((100% - (12px * 2)) / 3);
                max-width: 100%;
                margin-right: calc(${props.theme.spacing['24px']} / 2);
            }

            &:last-of-type {
                margin-right: 0;
            }
        }
    `
);

const FeatureWrapper = styled('div')(
    (props) => css`
        display: flex;
        margin: ${props.theme.spacing['8px']} 0 ${props.theme.spacing['16px']};
    `
);

const StyledFeaturePropertiesHeader = styled('div')(
    (props) => css`
        width: 100%;
        padding-bottom: calc(${props.theme.spacing['24px']} / 2);
    `
);

const StyledBadge = styled(Badge)(
    (props) => css`
        padding: ${props.theme.spacing['4px']} 12px;
        p {
            line-height: 1rem;
        }
    `
);

export {
    PropertyWrapper,
    TitleWrapper,
    AgentImage,
    StyledCard,
    PropertyDetails,
    StyledPropertyPrice,
    StyledPropertyPriceSmall,
    StyledPropertySubPrice,
    StyledPropertyTitle,
    StyledPropertyAddress,
    StyledPropertyAddressLarge,
    AgentImageWrapper,
    PropertyDetailsContainer,
    PropertyImage,
    FeatureWrapper,
    StyledFeaturePropertiesHeader,
    StyledBadge,
    FeaturePropertyCardWrapper
};
